import { useEffect, type ReactNode } from "react";
import { z } from "zod";
import { supabase, signInAnonymously } from "../../lib/supabase";
import { warmClaimsApi } from "../../lib/apiClients";
import { useDynamicConfig } from "../../lib/delivery";
import { DynamicConfigs } from "../../lib/delivery_keys";
import { useDocumentEventListener } from "../../hooks/useDocumentEventListener";
import { useForegroundInterval } from "../../hooks/useForegroundInterval";

const heartbeatConfigSchema = z.object({
  enabled: z.boolean(),
  intervalMs: z.number().int().min(5000),
});

interface Props {
  children: ReactNode;
}

/**
 * Manages the anonymous session lifecycle without proactively creating one.
 * Anonymous sessions are created lazily by claimsAPIClient on the first verification
 * request — this avoids generating a DB row for every visitor who bounces without
 * doing anything.
 *
 * Listens for SIGNED_OUT (expired refresh token) and re-establishes the anonymous
 * session so users who have already verified can continue without a page reload.
 */
export function SessionProvider({ children }: Props) {
  // Warm the claim-checking isolate on load (immediate: true) and keep it alive while the tab is
  // in the foreground via a periodic heartbeat. The interval + on/off switch live in a Statsig
  // dynamic config so they can be tuned or killed without a redeploy. While the config hasn't
  // loaded, enabled defaults to false — the mount-time warm still fires via immediate.
  const heartbeatConfig = useDynamicConfig(
    DynamicConfigs.claimCheckingHeartbeat,
    heartbeatConfigSchema,
    60_000
  );
  useForegroundInterval(warmClaimsApi, heartbeatConfig?.intervalMs ?? 20_000, {
    immediate: true,
    enabled: heartbeatConfig?.enabled ?? false,
  });

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        signInAnonymously().catch(console.error);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useDocumentEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      supabase.auth.refreshSession().catch(console.error);
    }
  });

  return <>{children}</>;
}
